import { useEffect, useState, useRef } from "react";
import api, { apiErr, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Button, Modal, Input, Select, Spinner, EmptyState, StatusBadge } from "@/components/common";
import { Plus, Trash2, FolderOpen, FileText, Upload, Download } from "lucide-react";

const CARTELLE = ["Statuto", "Atto costitutivo", "Verbali", "Soci", "Tesserati", "Istruttori", "Certificati", "Contratti", "Contabilità", "Assicurazioni", "Moduli Privacy"];

export default function Documenti() {
  const { can } = useAuth();
  const editable = can("documenti", "edit");
  const [items, setItems] = useState(null);
  const [active, setActive] = useState("Tutti");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ cartella: "Statuto", nome: "", file_id: "", scadenza: "", note: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/documenti").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_id: data.file_id, nome: f.nome || data.filename }));
      toast.success("File caricato");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };
  const save = async () => {
    if (!form.nome) { toast.error("Nome documento obbligatorio"); return; }
    try { await api.post("/documenti", form); toast.success("Documento salvato"); setOpen(false); setForm({ cartella: "Statuto", nome: "", file_id: "", scadenza: "", note: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/documenti/${id}`); load(); };

  if (!items) return <Spinner />;
  const cartelleCount = CARTELLE.map((c) => ({ c, n: items.filter((d) => d.cartella === c).length })).filter((x) => x.n > 0);
  const filtered = active === "Tutti" ? items : items.filter((d) => d.cartella === active);

  return (
    <div>
      <PageHeader title="Documenti" subtitle="Fascicolo digitale dell'ASD"
        actions={editable && <Button onClick={() => setOpen(true)} data-testid="add-documento-btn"><Plus size={16} /> Carica documento</Button>} />
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setActive("Tutti")} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${active === "Tutti" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>Tutti ({items.length})</button>
        {cartelleCount.map(({ c, n }) => (
          <button key={c} onClick={() => setActive(c)} data-testid={`cartella-${c}`} className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${active === c ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}><FolderOpen size={14} /> {c} ({n})</button>
        ))}
      </div>
      {filtered.length === 0 ? <Card className="p-6"><EmptyState icon={FolderOpen} title="Nessun documento" /></Card> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d) => (
            <Card key={d.id} className="p-4 flex items-start gap-3" data-testid={`documento-${d.id}`}>
              <div className="w-10 h-10 rounded-lg bg-slate-100 grid place-items-center shrink-0"><FileText size={20} className="text-slate-500" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-800 text-sm truncate">{d.nome}</p>
                <p className="text-xs text-slate-400">{d.cartella}</p>
                {d.scadenza && <div className="mt-1.5"><StatusBadge status={d.status} /></div>}
              </div>
              <div className="flex flex-col gap-1">
                {d.file_id && <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Download size={15} /></a>}
                {editable && <button onClick={() => del(d.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Carica documento">
        <div className="space-y-4">
          <Select label="Cartella" value={form.cartella} onChange={(e) => setForm((f) => ({ ...f, cartella: e.target.value }))} data-testid="doc-cartella">{CARTELLE.map((c) => <option key={c}>{c}</option>)}</Select>
          <Input label="Nome documento" data-testid="doc-nome" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} />
          <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50/40 transition" data-testid="doc-upload-zone">
            <Upload size={22} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-500">{uploading ? "Caricamento..." : form.file_id ? "✓ File caricato — clicca per sostituire" : "Clicca per caricare (PDF, immagini)"}</p>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => doUpload(e.target.files[0])} />
          </div>
          <Input label="Scadenza (opzionale)" type="date" value={form.scadenza} onChange={(e) => setForm((f) => ({ ...f, scadenza: e.target.value }))} />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="save-documento-btn">Salva</Button></div>
        </div>
      </Modal>
    </div>
  );
}
